/**
 * 2D Graph Layout Utility
 * 
 * Lightweight utility for rendering clusters of nodes with edges in a satisfying arrangement.
 * Uses force-directed layout algorithm for automatic positioning.
 */

export interface GraphNode {
  id: string;
  x?: number;
  y?: number;
  radius?: number;
  label?: string;
  [key: string]: any; // Allow additional properties
}

export interface GraphEdge {
  id: string;
  from: string;
  to: string;
  weight?: number; // Edge weight (affects attraction strength)
  [key: string]: any; // Allow additional properties
}

export interface GraphLayout {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface LayoutOptions {
  width: number;
  height: number;
  nodeRadius?: number;
  repulsionStrength?: number; // How much nodes repel each other
  attractionStrength?: number; // How much edges attract nodes
  centerAttraction?: number; // How much nodes are attracted to center
  iterations?: number; // Number of layout iterations
  damping?: number; // Velocity damping (0-1, lower = more damping)
  minDistance?: number; // Minimum distance between nodes
  clusterDistance?: number; // Distance threshold for clustering
}

const DEFAULT_OPTIONS: Required<LayoutOptions> = {
  width: 800,
  height: 600,
  nodeRadius: 20,
  repulsionStrength: 1000,
  attractionStrength: 0.1,
  centerAttraction: 0.01,
  iterations: 100,
  damping: 0.9,
  minDistance: 40,
  clusterDistance: 150
};

/**
 * Calculate initial positions for nodes (circular or grid arrangement)
 */
function initializePositions(
  nodes: GraphNode[],
  options: Required<LayoutOptions>
): Map<string, { x: number; y: number; vx: number; vy: number }> {
  const positions = new Map<string, { x: number; y: number; vx: number; vy: number }>();
  const centerX = options.width / 2;
  const centerY = options.height / 2;
  
  // Use circular arrangement for small graphs, grid for large
  if (nodes.length <= 10) {
    // Circular arrangement
    const angleStep = (2 * Math.PI) / nodes.length;
    const radius = Math.min(options.width, options.height) * 0.3;
    
    nodes.forEach((node, i) => {
      const angle = i * angleStep;
      positions.set(node.id, {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
        vx: 0,
        vy: 0
      });
    });
  } else {
    // Grid arrangement
    const cols = Math.ceil(Math.sqrt(nodes.length));
    const rows = Math.ceil(nodes.length / cols);
    const cellWidth = options.width / (cols + 1);
    const cellHeight = options.height / (rows + 1);
    
    nodes.forEach((node, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      positions.set(node.id, {
        x: cellWidth * (col + 1),
        y: cellHeight * (row + 1),
        vx: 0,
        vy: 0
      });
    });
  }
  
  return positions;
}

/**
 * Calculate distance between two points
 */
function distance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Apply force-directed layout algorithm
 */
export function calculateLayout(
  layout: GraphLayout,
  options: Partial<LayoutOptions> = {}
): GraphLayout {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { nodes, edges } = layout;
  
  if (nodes.length === 0) {
    return layout;
  }
  
  // Initialize positions and velocities
  const positions = initializePositions(nodes, opts);
  
  // Build adjacency map for efficient edge lookups
  const adjacencyMap = new Map<string, GraphEdge[]>();
  edges.forEach(edge => {
    if (!adjacencyMap.has(edge.from)) {
      adjacencyMap.set(edge.from, []);
    }
    if (!adjacencyMap.has(edge.to)) {
      adjacencyMap.set(edge.to, []);
    }
    adjacencyMap.get(edge.from)!.push(edge);
    adjacencyMap.get(edge.to)!.push(edge);
  });
  
  // Run force-directed layout iterations
  for (let iteration = 0; iteration < opts.iterations; iteration++) {
    // Calculate repulsion forces (nodes repel each other)
    for (let i = 0; i < nodes.length; i++) {
      const nodeA = nodes[i];
      const posA = positions.get(nodeA.id)!;
      
      for (let j = i + 1; j < nodes.length; j++) {
        const nodeB = nodes[j];
        const posB = positions.get(nodeB.id)!;
        
        const dx = posB.x - posA.x;
        const dy = posB.y - posA.y;
        const dist = Math.max(distance(posA.x, posA.y, posB.x, posB.y), 0.1);
        
        // Repulsion force (inverse square law)
        const force = opts.repulsionStrength / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        
        posA.vx -= fx;
        posA.vy -= fy;
        posB.vx += fx;
        posB.vy += fy;
      }
    }
    
    // Calculate attraction forces (edges attract connected nodes)
    edges.forEach(edge => {
      const posFrom = positions.get(edge.from);
      const posTo = positions.get(edge.to);
      
      if (!posFrom || !posTo) return;
      
      const dx = posTo.x - posFrom.x;
      const dy = posTo.y - posFrom.y;
      const dist = Math.max(distance(posFrom.x, posFrom.y, posTo.x, posTo.y), 0.1);
      
      // Ideal edge length (based on cluster distance)
      const idealLength = opts.clusterDistance;
      const weight = edge.weight || 1;
      
      // Attraction force (proportional to distance from ideal)
      const force = opts.attractionStrength * (dist - idealLength) * weight;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      
      posFrom.vx += fx;
      posFrom.vy += fy;
      posTo.vx -= fx;
      posTo.vy -= fy;
    });
    
    // Apply center attraction (prevents nodes from drifting off-screen)
    const centerX = opts.width / 2;
    const centerY = opts.height / 2;
    
    nodes.forEach(node => {
      const pos = positions.get(node.id)!;
      const dx = centerX - pos.x;
      const dy = centerY - pos.y;
      
      pos.vx += dx * opts.centerAttraction;
      pos.vy += dy * opts.centerAttraction;
    });
    
    // Apply minimum distance constraint
    for (let i = 0; i < nodes.length; i++) {
      const nodeA = nodes[i];
      const posA = positions.get(nodeA.id)!;
      
      for (let j = i + 1; j < nodes.length; j++) {
        const nodeB = nodes[j];
        const posB = positions.get(nodeB.id)!;
        
        const dist = distance(posA.x, posA.y, posB.x, posB.y);
        if (dist < opts.minDistance) {
          const dx = posB.x - posA.x;
          const dy = posB.y - posA.y;
          const angle = Math.atan2(dy, dx);
          const pushDistance = (opts.minDistance - dist) / 2;
          
          posA.x -= Math.cos(angle) * pushDistance;
          posA.y -= Math.sin(angle) * pushDistance;
          posB.x += Math.cos(angle) * pushDistance;
          posB.y += Math.sin(angle) * pushDistance;
        }
      }
    }
    
    // Update positions and apply damping
    nodes.forEach(node => {
      const pos = positions.get(node.id)!;
      
      // Update position
      pos.x += pos.vx;
      pos.y += pos.vy;
      
      // Apply damping
      pos.vx *= opts.damping;
      pos.vy *= opts.damping;
      
      // Keep nodes within bounds
      pos.x = Math.max(opts.nodeRadius, Math.min(opts.width - opts.nodeRadius, pos.x));
      pos.y = Math.max(opts.nodeRadius, Math.min(opts.height - opts.nodeRadius, pos.y));
    });
  }
  
  // Update nodes with final positions
  const updatedNodes = nodes.map(node => {
    const pos = positions.get(node.id);
    if (pos) {
      return {
        ...node,
        x: pos.x,
        y: pos.y,
        radius: node.radius || opts.nodeRadius
      };
    }
    return node;
  });
  
  return {
    nodes: updatedNodes,
    edges
  };
}

/**
 * Find clusters of nodes (groups of closely connected nodes)
 */
export function findClusters(
  layout: GraphLayout,
  options: Partial<LayoutOptions> = {}
): Map<string, number> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { nodes, edges } = layout;
  const clusters = new Map<string, number>();
  let clusterId = 0;
  
  // Build adjacency list
  const adjacency = new Map<string, Set<string>>();
  nodes.forEach(node => adjacency.set(node.id, new Set()));
  edges.forEach(edge => {
    adjacency.get(edge.from)?.add(edge.to);
    adjacency.get(edge.to)?.add(edge.from);
  });
  
  // DFS to find connected components
  const visited = new Set<string>();
  
  function dfs(nodeId: string, currentCluster: number) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    clusters.set(nodeId, currentCluster);
    
    const neighbors = adjacency.get(nodeId);
    if (neighbors) {
      neighbors.forEach(neighborId => {
        if (!visited.has(neighborId)) {
          dfs(neighborId, currentCluster);
        }
      });
    }
  }
  
  nodes.forEach(node => {
    if (!visited.has(node.id)) {
      dfs(node.id, clusterId++);
    }
  });
  
  return clusters;
}

/**
 * Get bounding box of all nodes
 */
export function getBoundingBox(nodes: GraphNode[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
} {
  if (nodes.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }
  
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  
  nodes.forEach(node => {
    if (node.x !== undefined && node.y !== undefined) {
      const radius = node.radius || 20;
      minX = Math.min(minX, node.x - radius);
      minY = Math.min(minY, node.y - radius);
      maxX = Math.max(maxX, node.x + radius);
      maxY = Math.max(maxY, node.y + radius);
    }
  });
  
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Scale and center layout to fit within given dimensions
 */
export function fitToBounds(
  layout: GraphLayout,
  targetWidth: number,
  targetHeight: number,
  padding: number = 20
): GraphLayout {
  const bbox = getBoundingBox(layout.nodes);
  
  if (bbox.width === 0 || bbox.height === 0) {
    return layout;
  }
  
  const scaleX = (targetWidth - padding * 2) / bbox.width;
  const scaleY = (targetHeight - padding * 2) / bbox.height;
  const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down
  
  const offsetX = (targetWidth - bbox.width * scale) / 2 - bbox.minX * scale;
  const offsetY = (targetHeight - bbox.height * scale) / 2 - bbox.minY * scale;
  
  const scaledNodes = layout.nodes.map(node => ({
    ...node,
    x: node.x !== undefined ? node.x * scale + offsetX : undefined,
    y: node.y !== undefined ? node.y * scale + offsetY : undefined
  }));
  
  return {
    nodes: scaledNodes,
    edges: layout.edges
  };
}

